import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityCard } from '@/components/EntityCard';
import { storyTemplatesPageMessages } from './storyTemplatesPage.messages';
import { fetchStoryTemplates } from '@/api/services/story-templates/fetchStoryTemplates';
import { softDeleteStoryTemplate } from '@/api/services/story-templates/softDeleteStoryTemplate';
import type { StoryTemplateWithUrls } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import { Button } from '@/components/ui/button';
import { StoryPreviewModal } from '@/components/StoryPreviewModal';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ROUTES } from '@/routes';
import { DashboardPageContainer } from '@/components/layout/DashboardPageContainer';
import { CardsGridContainer } from '@/components/layout/CardsGridContainer';
import { transformStoryTemplateToEntityCard } from '@/pages/StoryTemplatesPage/helpers/transformStoryTemplateToEntityCard';
import { sharedMessages } from '@/shared-messages/shared.messages';
import { toast } from 'sonner';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { useAuth } from '@/hooks/useAuth';

export function StoryTemplatesPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [storyTemplates, setStoryTemplates] = useState<StoryTemplateWithUrls[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(
    null
  );
  const [templateToDelete, setTemplateToDelete] =
    useState<StoryTemplateWithUrls | null>(null);

  // Preview modal state
  const [previewTemplate, setPreviewTemplate] =
    useState<StoryTemplateWithUrls | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const goToCreateTemplate = () => {
    navigate(ROUTES.DASHBOARD_STORY_TEMPLATES_CREATE);
  };

  const handleDeleteTemplate = (template: StoryTemplateWithUrls) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setDeletingTemplateId(templateToDelete.id);
      await softDeleteStoryTemplate(templateToDelete.id);

      // Remove the deleted template from the local state
      setStoryTemplates((prevTemplates) =>
        prevTemplates.filter((template) => template.id !== templateToDelete.id)
      );

      toast.success(storyTemplatesPageMessages.deleteSuccessMessage);
    } catch {
      toast.error(storyTemplatesPageMessages.deleteErrorMessage);
    } finally {
      setDeletingTemplateId(null);
      setTemplateToDelete(null);
    }
  };

  const cancelDeleteTemplate = () => {
    setTemplateToDelete(null);
  };

  const handleOpenPreview = (template: StoryTemplateWithUrls) => {
    setPreviewTemplate(template);
    setIsPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewModalOpen(false);
    setPreviewTemplate(null);
  };

  useLoadOnce(() => {
    const loadStoryTemplates = async () => {
      try {
        setLoading(true);

        const fetchedTemplates = await fetchStoryTemplates();
        setStoryTemplates(fetchedTemplates);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    loadStoryTemplates();
  });

  return (
    <DashboardPageContainer
      headline={storyTemplatesPageMessages.title}
      subheading={storyTemplatesPageMessages.subtitle}
      cta={
        isAdmin ? (
          <Button onClick={goToCreateTemplate}>
            {storyTemplatesPageMessages.createButton}
          </Button>
        ) : undefined
      }
    >
      <CardsGridContainer loading={loading}>
        {/* Custom Template Card - Always show first */}
        <EntityCard
          title={storyTemplatesPageMessages.customTemplateCardTitle}
          description={storyTemplatesPageMessages.customTemplateCardDescription}
          actions={[
            {
              actionButtonLabel:
                storyTemplatesPageMessages.customTemplateCardAction,
              onButtonClick: () => navigate(ROUTES.CREATE_CUSTOM_TEMPLATE),
            },
          ]}
        />
        {storyTemplates.map((template) => {
          const entityCardProps = transformStoryTemplateToEntityCard(template);
          const isDeleting = deletingTemplateId === template.id;
          return (
            <EntityCard
              key={template.id}
              {...entityCardProps}
              actions={[
                {
                  actionButtonLabel:
                    storyTemplatesPageMessages.createStoryAction,
                  onButtonClick: () =>
                    navigate(ROUTES.CREATE_STORY(template.id)),
                },
                {
                  actionButtonLabel:
                    storyTemplatesPageMessages.openPreviewAction,
                  onButtonClick: () => handleOpenPreview(template),
                },
                // Only show delete button to admin users
                ...(isAdmin
                  ? [
                      {
                        actionButtonLabel: isDeleting
                          ? storyTemplatesPageMessages.deleting
                          : storyTemplatesPageMessages.deleteAction,
                        onButtonClick: () => handleDeleteTemplate(template),
                      },
                    ]
                  : []),
              ]}
            />
          );
        })}
      </CardsGridContainer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={templateToDelete !== null}
        onOpenChange={cancelDeleteTemplate}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {storyTemplatesPageMessages.deleteConfirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {storyTemplatesPageMessages.deleteConfirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteTemplate}>
              {sharedMessages.commonButtons.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTemplate}
              disabled={deletingTemplateId !== null}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deletingTemplateId === templateToDelete?.id
                ? storyTemplatesPageMessages.deleting
                : storyTemplatesPageMessages.deleteAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Story Preview Modal */}
      <StoryPreviewModal
        storyTemplate={previewTemplate}
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreview}
      />
    </DashboardPageContainer>
  );
}
